import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { fetchUsers } from "../../redux/actions/usersActions";
import "./users.css";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Badge, Button, Drawer, Input, Pagination, Select } from "antd";
import noImagePerson from "../../assets/noImagePerson.png";
import { thumbProps } from "../../utils/imageUrl";
import { PageLoader } from "../Loader/loader";
import { fullName } from "../../utils/fullName";

const PLACE_TYPES = {
  schools: "Դպրոց",
  clubs: "Ակումբ",
  federation: "Մարզային ֆեդերացիա",
};

const EMPTY_FILTERS = { name: "", region: "", place_type: "", place_id: "", address: "" };

// Waits until the value stops changing, so typing does not fire a request per letter.
const useDebounced = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// True while the viewport is at most `query` wide; follows window resizes.
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

const Users = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const category = location.pathname.split("/")[3] || "";
  const [currentPage, setCurrentPage] = useState(1);
  const [role_type, setRole_type] = useState("any");
  const [sex, setSex] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ places: [], regions: [] });
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { users, loading, error } = useSelector((state) => state.users);

  const debouncedName = useDebounced(filters.name);
  const debouncedAddress = useDebounced(filters.address);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/judo-users/filters`)
      .then((res) => setOptions(res.data))
      .catch(() => setOptions({ places: [], regions: [] }));
  }, [API_BASE_URL]);

  // Organizations offered in the dropdown follow the chosen region and type.
  const placeOptions = useMemo(
    () =>
      options.places.filter(
        (p) =>
          (!filters.region || p.region === filters.region) &&
          (!filters.place_type || p.type === filters.place_type)
      ),
    [options.places, filters.region, filters.place_type]
  );

  const setFilter = (changes) => {
    setFilters((prev) => ({ ...prev, ...changes }));
    setCurrentPage(1);
  };

  const hasFilters =
    Object.values(filters).some(Boolean) || sex !== "all" || role_type !== "any";

  const activeFilterCount =
    [filters.region, filters.place_type, filters.place_id, filters.address].filter(Boolean).length +
    (sex !== "all" ? 1 : 0) +
    (role_type !== "any" ? 1 : 0);

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSex("all");
    setRole_type("any");
    setCurrentPage(1);
  };

  useEffect(() => {
    dispatch(
      fetchUsers(category, currentPage, "", sex, role_type, {
        name: debouncedName.trim(),
        address: debouncedAddress.trim(),
        region: filters.region,
        place_type: filters.place_type,
        place_id: filters.place_id,
      })
    );
  }, [
    dispatch,
    category,
    currentPage,
    sex,
    role_type,
    debouncedName,
    debouncedAddress,
    filters.region,
    filters.place_type,
    filters.place_id,
  ]);

  if (error)
    return (
      <p style={{ textAlign: "center", height: "100vh" }}>
        Ներողություն, առկա է տեխնիկական խնդիր
      </p>
    );

  const nameInput = (
    <Input
      size="large"
      className="members-filter f-name"
      allowClear
      prefix={<SearchOutlined />}
      placeholder="Անուն, ազգանուն"
      value={filters.name}
      onChange={(e) => setFilter({ name: e.target.value })}
    />
  );

  const otherFields = (
    <>
      <Select
        size="large"
        className="members-filter f-region"
        allowClear
        showSearch
        placeholder="Մարզ / քաղաք"
        value={filters.region || undefined}
        onChange={(value) => setFilter({ region: value || "", place_id: "" })}
        options={options.regions.map((r) => ({ value: r, label: r }))}
      />
      <Select
        size="large"
        className="members-filter f-type"
        allowClear
        placeholder="Տեսակ"
        value={filters.place_type || undefined}
        onChange={(value) => setFilter({ place_type: value || "", place_id: "" })}
        options={Object.entries(PLACE_TYPES).map(([value, label]) => ({ value, label }))}
      />
      <Select
        size="large"
        className="members-filter f-place"
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Դպրոց / Ակումբ / Ֆեդերացիա"
        value={filters.place_id ? Number(filters.place_id) : undefined}
        onChange={(value) => setFilter({ place_id: value || "" })}
        options={placeOptions.map((p) => ({ value: p.id, label: p.name }))}
        notFoundContent="Չի գտնվել"
      />
      <Input
      size="large"
        className="members-filter f-address"
        allowClear
        placeholder="Կազմակերպության հասցե"
        value={filters.address}
        onChange={(e) => setFilter({ address: e.target.value })}
      />
      <Select
        size="large"
        className="members-filter f-sex"
        value={sex}
        onChange={(value) => {
          setSex(value);
          setCurrentPage(1);
        }}
        options={[
          { value: "all", label: "Բոլորը" },
          { value: "female", label: "Իգական" },
          { value: "male", label: "Արական" },
        ]}
      />
      <Select
        size="large"
        className="members-filter f-role"
        value={role_type}
        onChange={(value) => {
          setRole_type(value);
          setCurrentPage(1);
        }}
        options={[
          { value: "any", label: "Բոլորը" },
          { value: "sportsman", label: "Մարզվողներ" },
          { value: "trainer", label: "Մարզիչ" },
          { value: "referee", label: "Մրցավար" },
        ]}
      />
    </>
  );

  return (
    <>
      {isMobile ? (
        <div className="members-filters mobile">
          {nameInput}
          <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
            <Button
            size="large"
              className="members-filter-btn"
              icon={<FilterOutlined />}
              onClick={() => setDrawerOpen(true)}
              aria-label="Ֆիլտրեր"
            />
          </Badge>
          <Drawer
            title="Ֆիլտրեր"
            placement="left"
            width="min(88vw, 360px)"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            footer={
              <div className="members-drawer-footer">
                <Button onClick={resetFilters} disabled={!hasFilters}>
                  Մաքրել
                </Button>
                <Button type="primary" onClick={() => setDrawerOpen(false)}>
                  Ցույց տալ ({users.total ?? 0})
                </Button>
              </div>
            }
          >
            <div className="members-drawer-fields">{otherFields}</div>
          </Drawer>
        </div>
      ) : (
        <div className="members-filters">
          {nameInput}
          {otherFields}
          {hasFilters && (
            <Button className="members-reset" onClick={resetFilters}>
              Մաքրել
            </Button>
          )}
        </div>
      )}
      <div className="mainContainer">
        <div className="usersListSection">
          {loading && !users.data ? (
            <PageLoader />
          ) : (
            <div className="usersList" style={{ opacity: loading ? 0.6 : 1 }}>
              {users.data && users.data.length > 0 ? (
                users.data.map((user) => (
                  <div key={user.id} className="usersCard">
                    <Link
                      to={`/judo-users/${user.id}`}
                      key={user.id}
                      style={{ textDecoration: "none" }}
                    >
                      <img
                        className="usersIamge"
                        {...(user?.image?.file_path
                          ? thumbProps(user.image.file_path)
                          : { src: noImagePerson })}
                        alt={user.firstname}
                      />
                      <div className="usersContent">
                        <p>
                          {fullName(user)}
                        </p>
                        <p>{user.role_type}</p>
                      </div>
                    </Link>
                  </div>
                ))
              ) : (
                <p>Ոչ ոք չի գտնվել։</p>
              )}
            </div>
          )}
        </div>
        <Pagination
          current={currentPage}
          pageSize={users.per_page || 12}
          total={users.total || 0}
          showSizeChanger={false}
          onChange={setCurrentPage}
          className="pagination"
        />
      </div>
    </>
  );
};

export default Users;
